import { useEffect, useRef, useState } from 'react';
import { TopBar } from '../components/TopBar';
import { Favicon } from '../components/Favicon';
import { ACCENT_STYLES } from '../accents';
import { ACCENTS, api, type AccentName, type Me, type System, type User } from '../api';

interface Props {
  me: Me;
  onSignOut: () => void;
}

type Draft = {
  id?: number;
  name: string;
  url: string;
  description: string;
  icon: string;
  accent: AccentName;
};

const EMPTY_DRAFT: Draft = { name: '', url: '', description: '', icon: '', accent: 'sky' };

export function AdminPage({ me, onSignOut }: Props) {
  const [systems, setSystems] = useState<System[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [editing, setEditing] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const editorRef = useRef<HTMLDivElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  async function refresh() {
    try {
      const [s, u] = await Promise.all([api.listSystems(), api.listUsers()]);
      setSystems(s);
      setUsers(u);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  function startEdit(system: System) {
    setEditing(system.id);
    setDraft({
      id: system.id,
      name: system.name,
      url: system.url,
      description: system.description,
      icon: system.icon,
      accent: system.accent,
    });
    setError(null);
    requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    });
  }

  function cancelEdit() {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
  }

  async function saveDraft() {
    setError(null);
    try {
      const payload = {
        name: draft.name.trim(),
        url: draft.url.trim(),
        description: draft.description.trim(),
        icon: draft.icon.trim(),
        accent: draft.accent,
      };
      if (editing !== null) {
        await api.updateSystem(editing, payload);
      } else {
        await api.createSystem(payload);
      }
      cancelEdit();
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function remove(id: number) {
    if (!confirm('Delete this system?')) return;
    await api.deleteSystem(id);
    await refresh();
  }

  async function move(id: number, dir: -1 | 1) {
    const idx = systems.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const next = idx + dir;
    if (next < 0 || next >= systems.length) return;
    const reordered = [...systems];
    [reordered[idx], reordered[next]] = [reordered[next], reordered[idx]];
    setSystems(reordered);
    await api.reorderSystems(reordered.map((s) => s.id));
  }

  async function addAdmin() {
    setError(null);
    try {
      await api.addAdmin(newAdminEmail.trim());
      setNewAdminEmail('');
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function removeAdmin(id: number) {
    await api.removeAdmin(id);
    await refresh();
  }

  return (
    <div className="relative min-h-screen paper-grain">
      <TopBar me={me} onSignOut={onSignOut} />
      <main className="mx-auto max-w-7xl space-y-16 px-6 py-12 md:px-10 md:py-16">
        {/* Hero band */}
        <header className="border-y hair-strong py-8 animate-rise">
          <p className="ix mb-3">§ Editor desk</p>
          <h1 className="font-display text-6xl font-light leading-none tracking-tightest text-cream md:text-7xl">
            Edit the{' '}
            <span
              className="italic text-coral"
              style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'WONK' 1" }}
            >
              issue.
            </span>
          </h1>
          <p className="mt-5 max-w-2xl text-pretty text-[15px] leading-relaxed text-cream-700">
            Curate the systems list, set their accents, and choose who else gets the editor key.
          </p>
        </header>

        {error && (
          <div className="border-l-2 border-coral bg-coral/10 px-4 py-3 font-mono text-[12px] uppercase tracking-wider text-coral-300">
            {error}
          </div>
        )}

        {/* ───────── Systems editor ───────── */}
        <section>
          <SectionHeader number="01" title="Systems" count={`${systems.length} entries`} />

          <div className="mt-6 overflow-hidden border hair">
            <table className="w-full text-sm">
              <thead className="border-b hair bg-ink-800 font-mono text-[10px] uppercase tracking-widestest text-cream-500">
                <tr>
                  <th className="px-5 py-3 text-left">№</th>
                  <th className="px-5 py-3 text-left">System</th>
                  <th className="px-5 py-3 text-left">URL</th>
                  <th className="px-5 py-3 text-left">Accent</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {systems.map((system, i) => {
                  const accent = ACCENT_STYLES[system.accent] ?? ACCENT_STYLES.slate;
                  const isEditing = editing === system.id;
                  return (
                    <tr
                      key={system.id}
                      className={`relative border-b hair last:border-b-0 transition-colors ${
                        isEditing ? 'bg-coral/10' : 'hover:bg-ink-700/60'
                      }`}
                    >
                      <td className="relative px-5 py-4 font-mono text-[11px] uppercase tracking-widestest text-cream-500">
                        <span
                          className={`absolute inset-y-0 left-0 w-1 ${accent.strip}`}
                          aria-hidden
                        />
                        {String(i + 1).padStart(2, '0')}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-4">
                          <Favicon
                            url={system.url}
                            name={system.name}
                            icon={system.icon}
                            accent={system.accent}
                            size={12}
                          />
                          <div>
                            <div className="font-display text-base font-medium tracking-tightish text-cream">
                              {system.name}
                            </div>
                            <div className="mt-0.5 line-clamp-1 text-xs text-cream-500">
                              {system.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <a
                          href={system.url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-[11px] text-cream-700 transition hover:text-coral"
                        >
                          {system.url}
                        </a>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`rounded-sm px-2 py-1 font-mono text-[10px] uppercase tracking-widestest ${accent.chip}`}
                        >
                          {system.accent}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <IconBtn label="Move up" disabled={i === 0} onClick={() => move(system.id, -1)}>
                            ↑
                          </IconBtn>
                          <IconBtn
                            label="Move down"
                            disabled={i === systems.length - 1}
                            onClick={() => move(system.id, 1)}
                          >
                            ↓
                          </IconBtn>
                          <IconBtn label="Edit" onClick={() => startEdit(system)}>
                            ✎
                          </IconBtn>
                          <IconBtn label="Delete" onClick={() => remove(system.id)} danger>
                            ×
                          </IconBtn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Editor form */}
          <div
            ref={editorRef}
            className={`mt-8 border-l-2 bg-ink-800 p-6 transition-colors md:p-8 ${
              editing !== null ? 'border-coral' : 'border-cream-200'
            }`}
          >
            <div className="mb-6 flex items-baseline justify-between border-b hair pb-4">
              <h3 className="font-display text-2xl font-medium tracking-tightish text-cream">
                {editing !== null
                  ? `Editing ${systems.find((s) => s.id === editing)?.name ?? `#${editing}`}`
                  : 'Add a new system'}
              </h3>
              <span className="font-mono text-[10px] uppercase tracking-widestest text-cream-500">
                {editing !== null ? 'edit mode' : 'new entry'}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <Field label="Name">
                <input
                  ref={nameInputRef}
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Sales"
                  className={INPUT}
                />
              </Field>
              <Field label="URL">
                <input
                  value={draft.url}
                  onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                  placeholder="https://sales.surfboard.cloud/"
                  className={INPUT}
                />
              </Field>
              <Field label="Icon (1–4 chars)">
                <input
                  value={draft.icon}
                  maxLength={4}
                  onChange={(e) => setDraft({ ...draft, icon: e.target.value })}
                  placeholder="S"
                  className={INPUT}
                />
              </Field>
              <Field label="Accent">
                <div className="flex flex-wrap gap-2 pt-1">
                  {ACCENTS.map((a) => {
                    const style = ACCENT_STYLES[a];
                    const active = draft.accent === a;
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => setDraft({ ...draft, accent: a })}
                        title={a}
                        aria-label={a}
                        className={`relative h-9 w-9 rounded-sm transition-transform hover:-translate-y-0.5 ${style.strip} ${
                          active
                            ? 'outline outline-2 outline-cream outline-offset-2'
                            : 'outline outline-1 outline-transparent'
                        }`}
                      />
                    );
                  })}
                </div>
              </Field>
              <Field label="Description" className="md:col-span-2">
                <textarea
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="One-sentence summary of what this system does."
                  rows={2}
                  className={`${INPUT} resize-none`}
                />
              </Field>
            </div>
            <div className="mt-7 flex items-center justify-end gap-3 border-t hair pt-5">
              {editing !== null && (
                <button
                  onClick={cancelEdit}
                  className="font-mono text-[11px] uppercase tracking-widestest text-cream-500 transition hover:text-cream"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={saveDraft}
                disabled={!draft.name.trim() || !draft.url.trim()}
                className="group inline-flex items-center gap-3 rounded-sm bg-coral px-5 py-3 font-mono text-[12px] uppercase tracking-widestest text-ink transition hover:bg-coral-400 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-coral"
              >
                {editing !== null ? 'Save changes' : 'Add system'}
                <span className="transition-transform group-hover:translate-x-0.5" aria-hidden>
                  →
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* ───────── Admins ───────── */}
        <section>
          <SectionHeader
            number="02"
            title="Admins"
            count={`${users.filter((u) => u.isAdmin).length} admin · ${users.length} total`}
          />

          <div className="mt-6 overflow-hidden border hair">
            <table className="w-full text-sm">
              <thead className="border-b hair bg-ink-800 font-mono text-[10px] uppercase tracking-widestest text-cream-500">
                <tr>
                  <th className="px-5 py-3 text-left">Email</th>
                  <th className="px-5 py-3 text-left">Last sign-in</th>
                  <th className="px-5 py-3 text-left">Role</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b hair last:border-b-0 hover:bg-ink-700/60">
                    <td className="px-5 py-4">
                      <div className="font-display text-base font-medium tracking-tightish text-cream">
                        {user.email}
                      </div>
                      {user.name && <div className="mt-0.5 text-xs text-cream-500">{user.name}</div>}
                    </td>
                    <td className="px-5 py-4 font-mono text-[11px] text-cream-700">
                      {user.lastLoginAt ? (
                        new Date(user.lastLoginAt + 'Z').toLocaleString()
                      ) : (
                        <span className="italic text-cream-300">never</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`rounded-sm px-2 py-1 font-mono text-[10px] uppercase tracking-widestest ${
                          user.isAdmin
                            ? 'bg-coral/15 text-coral-300'
                            : 'bg-cream-200/40 text-cream-500'
                        }`}
                      >
                        {user.isAdmin ? 'admin' : 'reader'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {user.isAdmin && user.id !== me.id && (
                          <IconBtn label="Revoke admin" onClick={() => removeAdmin(user.id)} danger>
                            ×
                          </IconBtn>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 flex flex-col gap-4 border-l-2 border-sage bg-ink-800 p-6 sm:flex-row sm:items-end md:p-8">
            <Field label="Promote email to admin" className="flex-1">
              <input
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                placeholder="teammate@surfboard.se"
                className={INPUT}
                onKeyDown={(e) => e.key === 'Enter' && newAdminEmail && addAdmin()}
              />
            </Field>
            <button
              onClick={addAdmin}
              disabled={!newAdminEmail.trim()}
              className="group inline-flex items-center gap-3 rounded-sm bg-sage px-5 py-3 font-mono text-[12px] uppercase tracking-widestest text-ink transition hover:bg-sage-400 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-sage"
            >
              Grant admin
              <span className="transition-transform group-hover:translate-x-0.5" aria-hidden>
                →
              </span>
            </button>
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-widestest text-cream-500">
            They become admin on their next sign-in (or instantly, if they already have an account).
          </p>
        </section>
      </main>
    </div>
  );
}

const INPUT =
  'w-full rounded-sm border hair bg-ink-900 px-4 py-3 text-[14px] text-cream placeholder:text-cream-300 transition focus:border-coral focus:outline-none focus:ring-1 focus:ring-coral';

function SectionHeader({ number, title, count }: { number: string; title: string; count: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 border-b hair-strong pb-3">
      <div className="flex items-baseline gap-4">
        <span className="font-mono text-[11px] uppercase tracking-widestest text-coral">§ {number}</span>
        <h2 className="font-display text-3xl font-medium tracking-tightish text-cream">{title}</h2>
      </div>
      <span className="font-mono text-[11px] uppercase tracking-widestest text-cream-500">{count}</span>
    </div>
  );
}

function Field({
  label,
  children,
  className = '',
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 block font-mono text-[10px] uppercase tracking-widestest text-cream-500">
        {label}
      </span>
      {children}
    </label>
  );
}

function IconBtn({
  children,
  onClick,
  label,
  disabled,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={`grid h-8 w-8 place-items-center rounded-sm border hair text-sm transition disabled:cursor-not-allowed disabled:opacity-30 ${
        danger ? 'text-coral hover:border-coral hover:bg-coral/15' : 'text-cream-700 hover:border-cream-300/30 hover:text-cream'
      }`}
    >
      {children}
    </button>
  );
}
